import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    Typography,
    IconButton,
    TextField,
    Card,
    CardContent,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@material-ui/core';
import { Cancel, Send, SkipNext, SkipPrevious, RotateLeft, RotateRight, Flip, TextFields, CropSquare, RadioButtonUnchecked, ShowChart, CallMade, Undo, DeleteForever, Add, Remove, FormatColorFill, Stop } from '@material-ui/icons';
import AudioModal from '../AudioModal';
import { Document, Page, pdfjs } from 'react-pdf';
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { grey } from '@material-ui/core/colors';
import { InputAdornment, InputBase, LinearProgress } from '@material-ui/core';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    modal: {
        background: theme.palette.background.default,
    },
    headerBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1),
        background: theme.palette.background.paper,
        borderRadius: 8,
        marginBottom: theme.spacing(1)
    },
    messageInputWrapperPrivate: {
        padding: 6,
        marginRight: 7,
        background: theme.palette.background.paper,
        display: "flex",
        borderRadius: 20,
        flex: 1,
        position: "relative",
    },
    buttonEnable: {
        color: theme.mode === 'light' ? "grey" : "#eee",
    },
    buttonDisable: {
        color: grey[400]
    },
    toolsBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: theme.spacing(1),
        background: theme.palette.background.paper,
        borderRadius: 8,
        marginTop: theme.spacing(1)
    },
    canvasWrap: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 400,
        background: theme.palette.background.default,
        borderRadius: 8
    },
    captionWrap: {
        marginTop: theme.spacing(1)
    },
    textOptions: {
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
        marginTop: 8,
        alignItems: 'center'
    },
    optionsRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 8,
        flexWrap: 'wrap',
        justifyContent: 'center'
    },
    optionsContainer: {
        width: '100%',
        padding: '0 8px'
    },
    hintCentered: {
        width: '100%',
        textAlign: 'center',
        marginTop: 4
    },
    dotPalette: {
        display: 'flex',
        alignItems: 'center',
        gap: 6
    },
    colorDot: {
        width: 22,
        height: 22,
        borderRadius: '50%',
        border: `2px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        boxSizing: 'border-box'
    },
    colorDotSelected: {
        boxShadow: `0 0 0 2px ${theme.palette.primary.main}`
    },
    smallNumberBox: {
        width: 56,
        padding: '2px 6px',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        background: theme.palette.background.paper,
        color: theme.palette.text.primary
    }
}));

const MessageUploadMedias = ({ isOpen, files, onClose, onSend, onCancelSelection, uploading = false, progressList = [], onCancelUpload, initialCaptions = null }) => {
    const classes = useStyles();
    const theme = useTheme();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [captions, setCaptions] = useState(() => {
        if (Array.isArray(initialCaptions) && initialCaptions.length) {
            // Preenche legendas iniciais; completa com strings vazias se necessário
            const base = files.map(() => '');
            initialCaptions.forEach((c, idx) => { if (idx < base.length) base[idx] = c || ''; });
            return base;
        }
        return files.map(() => '');
    });
    const [numPages, setNumPages] = React.useState(null);
    const [isTyping, setIsTyping] = useState(false);
    const canvasRef = useRef(null);
    const isMouseDownRef = useRef(false);
    const draggingTextRef = useRef(false);
    const currentShapeRef = useRef(null);
    const canvasReadyRedrawRef = useRef(true);
    const lastMoveTsRef = useRef(0);
    const [edits, setEdits] = useState(() => files.map(() => ({
        rotate: 0,
        flipH: false,
        flipV: false,
        text: { value: '', size: 28, color: '#FFFFFF', bg: true, x: 0.5, y: 0.5 },
        shapes: [],
        tool: 'none', // none | text | line | arrow | rect | circle | redact
        shapeColor: '#FF0000',
        strokeWidth: 3,
        fillShape: false,
    })));
    const [showTextOptions, setShowTextOptions] = useState(false);
    const [showShapeOptions, setShowShapeOptions] = useState(false);
    const [previewTick, setPreviewTick] = useState(0);
    
    // helpers de canvas
    const getCtx = () => canvasRef.current?.getContext('2d');
    const getPointer = (evt) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const x = (evt.clientX - rect.left);
        const y = (evt.clientY - rect.top);
        return { x, y, nx: x / (canvas.width || 1), ny: y / (canvas.height || 1) };
    };

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    const handleClose = () => {
        onClose();
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleNext = () => {
        if (currentIndex < files.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handleTextFieldBlur = () => {
        setIsTyping(false);
    };

    const handleCaptionChange = (e) => {
        const value = e.target.value;
        setCaptions((prevCaptions) => {
            const updatedCaptions = [...prevCaptions];
            updatedCaptions[currentIndex] = value;
            return updatedCaptions;
        });
        setIsTyping(true);
    };

    const updateEdit = (patch) => {
        setEdits(prev => {
            const next = [...prev];
            next[currentIndex] = { ...next[currentIndex], ...patch };
            return next;
        });
    };

    const rotateBy = (deg) => {
        const cur = edits[currentIndex];
        updateEdit({ rotate: (((cur.rotate || 0) + deg) % 360 + 360) % 360 });
    };

    const toggleFlipH = () => updateEdit({ flipH: !edits[currentIndex].flipH });
    const toggleFlipV = () => updateEdit({ flipV: !edits[currentIndex].flipV });

    const setTextValue = (v) => {
        const prev = edits[currentIndex]?.text || {};
        const canvas = canvasRef.current;
        const patch = { text: { ...prev, value: v } };
        // Se for a primeira inserção (antes estava vazio) centraliza no canvas
        if ((!prev.value || !prev.value.trim()) && v && v.trim() && canvas) {
            try {
                const ctx = canvas.getContext('2d');
                const fontSize = Math.max(12, Math.min(128, prev.size || 28));
                ctx.font = `${fontSize}px sans-serif`;
                const textW = ctx.measureText(v).width;
                const xPx = Math.max(0, (canvas.width - textW) / 2);
                const yPx = canvas.height / 2; // baseline ao meio
                patch.text.x = Math.max(0, Math.min(1, xPx / canvas.width));
                patch.text.y = Math.max(0, Math.min(1, yPx / canvas.height));
            } catch {}
        }
        updateEdit(patch);
    };
    const setTextSize = (v) => updateEdit({ text: { ...edits[currentIndex].text, size: Number(v) } });
    const setTextColor = (v) => updateEdit({ text: { ...edits[currentIndex].text, color: v } });
    const setTextBg = (v) => updateEdit({ text: { ...edits[currentIndex].text, bg: v } });
    const setTextPos = (v) => updateEdit({ text: { ...edits[currentIndex].text, position: v } });

    // Helpers de UI (paletas e clamps)
    const TEXT_COLORS = ['#FFFFFF', '#000000', '#FF0000', '#00FF00', '#00BFFF', '#FFD700', '#FFA500', '#800080'];
    const SHAPE_COLORS = ['#FF0000', '#00BFFF', '#00FF00', '#000000', '#FFFFFF', '#FFA500', '#800080'];
    const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
    const incTextSize = (d) => {
        const cur = edits[currentIndex]?.text?.size || 28;
        setTextSize(clamp(cur + d, 12, 128));
    };
    const setStrokeWidth = (n) => updateEdit({ strokeWidth: clamp(Number(n) || 1, 1, 10) });
    const incStrokeWidth = (d) => {
        const cur = edits[currentIndex]?.strokeWidth || 3;
        setStrokeWidth(clamp(cur + d, 1, 10));
    };

    // Desenhar preview no canvas para imagens
    useEffect(() => {
        const currentFile = files[currentIndex];
        if (!currentFile || !currentFile.type?.startsWith('image')) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const img = new Image();
        const url = URL.createObjectURL(currentFile);
        img.onload = () => {
            const maxW = 600, maxH = 400;
            const rotDeg = (edits[currentIndex]?.rotate || 0) % 360;
            const angle = rotDeg * Math.PI / 180;
            const imgW = img.width;
            const imgH = img.height;

            // 1) Reduz base se imagem for gigante (performance)
            const maxStage = 2048;
            const scaleDown = Math.min(1, maxStage / Math.max(imgW, imgH));
            const baseW = Math.max(1, Math.round(imgW * scaleDown));
            const baseH = Math.max(1, Math.round(imgH * scaleDown));
            const base = document.createElement('canvas');
            base.width = baseW; base.height = baseH;
            const bctx = base.getContext('2d');
            bctx.imageSmoothingEnabled = true;
            bctx.drawImage(img, 0, 0, baseW, baseH);

            // 2) Aplica rotação/flip em offscreen "stage"
            const rotatedW = (rotDeg === 90 || rotDeg === 270) ? baseH : baseW;
            const rotatedH = (rotDeg === 90 || rotDeg === 270) ? baseW : baseH;
            const stage = document.createElement('canvas');
            stage.width = Math.max(1, rotatedW);
            stage.height = Math.max(1, rotatedH);
            const sctx = stage.getContext('2d');
            sctx.imageSmoothingEnabled = true;
            sctx.save();
            sctx.translate(stage.width / 2, stage.height / 2);
            sctx.rotate(angle);
            const flipH = edits[currentIndex]?.flipH ? -1 : 1;
            const flipV = edits[currentIndex]?.flipV ? -1 : 1;
            sctx.scale(flipH, flipV);
            sctx.drawImage(base, -baseW / 2, -baseH / 2);
            sctx.restore();

            // 3) Desenha "stage" ajustado ao canvas principal
            canvas.width = maxW;
            canvas.height = maxH;
            // Reset de estado gráfico para evitar sobreposição/alpha residual
            ctx.globalAlpha = 1;
            ctx.globalCompositeOperation = 'source-over';
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.imageSmoothingEnabled = true;
            const fit = Math.min(canvas.width / stage.width, canvas.height / stage.height) || 1;
            const dW = stage.width * fit;
            const dH = stage.height * fit;
            const dx = (canvas.width - dW) / 2;
            const dy = (canvas.height - dH) / 2;
            ctx.drawImage(stage, dx, dy, dW, dH);

            // Texto (overlay) em coords do canvas
            const t = edits[currentIndex]?.text || {};
            if (t.value && t.value.trim()) {
                const fontSize = Math.max(12, Math.min(48, t.size || 28));
                ctx.font = `${fontSize}px sans-serif`;
                ctx.textBaseline = 'alphabetic';
                const metrics = ctx.measureText(t.value);
                const textW = metrics.width;
                const textH = fontSize;
                let x = Math.min(Math.max((t.x ?? 0.85) * canvas.width, 0), canvas.width - textW);
                let y = Math.min(Math.max((t.y ?? 0.90) * canvas.height, textH), canvas.height);
                if (t.bg) {
                    ctx.fillStyle = 'rgba(0,0,0,0.5)';
                    ctx.fillRect(x - 6, y - textH - 6, textW + 12, textH + 12);
                }
                ctx.fillStyle = t.color || '#FFFFFF';
                ctx.fillText(t.value, x, y);
            }

            // Desenhar shapes
            const drawShapes = (shape) => {
                const color = shape.color || '#FF0000';
                const sw = Math.max(1, shape.strokeWidth || 3);
                const sx = shape.start.x * canvas.width;
                const sy = shape.start.y * canvas.height;
                const ex = shape.end.x * canvas.width;
                const ey = shape.end.y * canvas.height;
                ctx.lineWidth = sw;
                ctx.strokeStyle = color;
                ctx.fillStyle = color;
                switch (shape.type) {
                    case 'line':
                        ctx.beginPath();
                        ctx.moveTo(sx, sy);
                        ctx.lineTo(ex, ey);
                        ctx.stroke();
                        break;
                    case 'arrow': {
                        ctx.beginPath();
                        ctx.moveTo(sx, sy);
                        ctx.lineTo(ex, ey);
                        ctx.stroke();
                        const a = Math.atan2(ey - sy, ex - sx);
                        const headLen = 8 + sw * 2;
                        ctx.beginPath();
                        ctx.moveTo(ex, ey);
                        ctx.lineTo(ex - headLen * Math.cos(a - Math.PI / 6), ey - headLen * Math.sin(a - Math.PI / 6));
                        ctx.lineTo(ex - headLen * Math.cos(a + Math.PI / 6), ey - headLen * Math.sin(a + Math.PI / 6));
                        ctx.lineTo(ex, ey);
                        ctx.fill();
                        break;
                    }
                    case 'rect': {
                        const w = ex - sx;
                        const h = ey - sy;
                        if (shape.fill) {
                            ctx.globalAlpha = 0.15;
                            ctx.fillRect(Math.min(sx, ex), Math.min(sy, ey), Math.abs(w), Math.abs(h));
                            ctx.globalAlpha = 1;
                        }
                        ctx.strokeRect(Math.min(sx, ex), Math.min(sy, ey), Math.abs(w), Math.abs(h));
                        break;
                    }
                    case 'redact': {
                        // Tarja sólida: preenchimento 100%, sem contorno
                        const w = ex - sx;
                        const h = ey - sy;
                        ctx.save();
                        ctx.globalAlpha = 1;
                        ctx.fillStyle = color || '#000000';
                        ctx.fillRect(Math.min(sx, ex), Math.min(sy, ey), Math.abs(w), Math.abs(h));
                        ctx.restore();
                        break;
                    }
                    case 'circle': {
                        const dx = ex - sx;
                        const dy = ey - sy;
                        const r = Math.sqrt(dx * dx + dy * dy);
                        ctx.beginPath();
                        ctx.arc(sx, sy, r, 0, Math.PI * 2);
                        if (shape.fill) {
                            ctx.globalAlpha = 0.15;
                            ctx.fill();
                            ctx.globalAlpha = 1;
                        }
                        ctx.stroke();
                        break;
                    }
                    default:
                        break;
                }
            };
            const shapes = edits[currentIndex]?.shapes || [];
            shapes.forEach(drawShapes);
            if (currentShapeRef.current) drawShapes(currentShapeRef.current);
            URL.revokeObjectURL(url);
        };
        img.onerror = () => URL.revokeObjectURL(url);
        img.src = url;
    }, [currentIndex, edits, files, previewTick]);

    // Quando o modal abre ou o índice/arquivos mudam, agenda redraw no próximo frame (garante 1º render)
    useEffect(() => {
        if (!isOpen) return;
        const id = typeof requestAnimationFrame !== 'undefined'
            ? requestAnimationFrame(() => setPreviewTick((t) => t + 1))
            : setTimeout(() => setPreviewTick((t) => t + 1), 0);
        return () => {
            if (typeof cancelAnimationFrame !== 'undefined' && typeof id === 'number') {
                cancelAnimationFrame(id);
            } else if (typeof id === 'number') {
                clearTimeout(id);
            }
        };
    }, [isOpen, currentIndex, files]);

    // Garante um redesenho assim que o canvas montar, evitando canvas "branco" até algum setState ocorrer
    useEffect(() => {
        if (!canvasRef.current) return;
        if (canvasReadyRedrawRef.current) return;
        canvasReadyRedrawRef.current = true;
        // Força uma atualização mínima para disparar o efeito de desenho
        setEdits(prev => [...prev]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvasRef.current]);

    // Ao trocar de arquivo ou abrir modal, reseta estados temporários de desenho
    useEffect(() => {
        currentShapeRef.current = null;
        draggingTextRef.current = false;
        isMouseDownRef.current = false;
    }, [currentIndex, isOpen]);

    const renderEditedImageToBlob = async (file, edit) => {
        const img = await new Promise((resolve, reject) => {
            const i = new Image();
            const url = URL.createObjectURL(file);
            i.onload = () => { URL.revokeObjectURL(url); resolve(i); };
            i.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
            i.src = url;
        });

        // calcular dimensões após rotação
        const rot = (edit?.rotate || 0) % 360;
        const flipH = edit?.flipH ? -1 : 1;
        const flipV = edit?.flipV ? -1 : 1;
        const origW = img.width;
        const origH = img.height;
        const rotatedW = (rot === 90 || rot === 270) ? origH : origW;
        const rotatedH = (rot === 90 || rot === 270) ? origW : origH;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = rotatedW;
        canvas.height = rotatedH;

        ctx.save();
        // mover para centro e aplicar transformações para desenhar imagem original
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(rot * Math.PI / 180);
        ctx.scale(flipH, flipV);
        // desenhar a imagem centralizada respeitando rotação
        const dw = (rot === 90 || rot === 270) ? origH : origW;
        const dh = (rot === 90 || rot === 270) ? origW : origH;
        ctx.drawImage(img, -dw / 2, -dh / 2);
        ctx.restore();

        // Texto com posição arrastada (coords normalizadas)
        const t = edit?.text || {};
        if (t.value && t.value.trim()) {
            const pad = Math.max(8, Math.round(Math.min(canvas.width, canvas.height) * 0.01));
            const fontSize = Math.max(12, Math.min(128, t.size || 28));
            ctx.font = `${fontSize}px sans-serif`;
            ctx.textBaseline = 'alphabetic';
            const metrics = ctx.measureText(t.value);
            const textW = metrics.width;
            const textH = fontSize;
            let x = Math.min(Math.max((t.x ?? 0.85) * (canvas.width || 1), pad), canvas.width - textW - pad);
            let y = Math.min(Math.max((t.y ?? 0.90) * (canvas.height || 1), pad + textH), canvas.height - pad);
            if (t.bg) {
                ctx.fillStyle = 'rgba(0,0,0,0.5)';
                ctx.fillRect(x - 12, y - textH - 12, textW + 24, textH + 24);
            }
            ctx.fillStyle = t.color || '#FFFFFF';
            ctx.fillText(t.value, x, y);
        }

        // Formas desenhadas
        const shapes = Array.isArray(edit?.shapes) ? edit.shapes : [];
        const drawShapes = (shape) => {
            const color = shape.color || '#FF0000';
            const sw = Math.max(1, shape.strokeWidth || 3);
            const sx = shape.start.x * canvas.width;
            const sy = shape.start.y * canvas.height;
            const ex = shape.end.x * canvas.width;
            const ey = shape.end.y * canvas.height;
            ctx.lineWidth = sw;
            ctx.strokeStyle = color;
            ctx.fillStyle = color;
            switch (shape.type) {
                case 'line':
                    ctx.beginPath();
                    ctx.moveTo(sx, sy);
                    ctx.lineTo(ex, ey);
                    ctx.stroke();
                    break;
                case 'arrow': {
                    ctx.beginPath();
                    ctx.moveTo(sx, sy);
                    ctx.lineTo(ex, ey);
                    ctx.stroke();
                    const a = Math.atan2(ey - sy, ex - sx);
                    const headLen = 8 + sw * 2;
                    ctx.beginPath();
                    ctx.moveTo(ex, ey);
                    ctx.lineTo(ex - headLen * Math.cos(a - Math.PI / 6), ey - headLen * Math.sin(a - Math.PI / 6));
                    ctx.lineTo(ex - headLen * Math.cos(a + Math.PI / 6), ey - headLen * Math.sin(a + Math.PI / 6));
                    ctx.lineTo(ex, ey);
                    ctx.fill();
                    break;
                }
                case 'rect': {
                    const w = ex - sx;
                    const h = ey - sy;
                    if (shape.fill) {
                        ctx.globalAlpha = 0.15;
                        ctx.fillRect(Math.min(sx, ex), Math.min(sy, ey), Math.abs(w), Math.abs(h));
                        ctx.globalAlpha = 1;
                    }
                    ctx.strokeRect(Math.min(sx, ex), Math.min(sy, ey), Math.abs(w), Math.abs(h));
                    break;
                }
                        case 'redact': {
                            // Tarja sólida: preenchimento 100%, sem contorno
                            const w = ex - sx;
                            const h = ey - sy;
                            ctx.save();
                            ctx.globalAlpha = 1;
                            ctx.fillStyle = color || '#000000';
                            ctx.fillRect(Math.min(sx, ex), Math.min(sy, ey), Math.abs(w), Math.abs(h));
                            ctx.restore();
                            break;
                        }
                case 'circle': {
                    const dx = ex - sx;
                    const dy = ey - sy;
                    const r = Math.sqrt(dx * dx + dy * dy);
                    ctx.beginPath();
                    ctx.arc(sx, sy, r, 0, Math.PI * 2);
                    if (shape.fill) {
                        ctx.globalAlpha = 0.15;
                        ctx.fill();
                        ctx.globalAlpha = 1;
                    }
                    ctx.stroke();
                    break;
                }
                default:
                    break;
            }
        };
        shapes.forEach(drawShapes);

        const mime = file.type && file.type.startsWith('image/') ? file.type : 'image/jpeg';
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, mime, 0.92));
        // preservar o nome original
        const name = file.name || `edited_${Date.now()}.jpg`;
        return new File([blob], name, { type: blob.type });
    };

    const handleSend = async () => {
        const selectedMedias = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            let outFile = file;
            if (file.type?.startsWith('image')) {
                try {
                    outFile = await renderEditedImageToBlob(file, edits[i]);
                } catch (e) {
                    // fallback para arquivo original em caso de erro
                    outFile = file;
                }
            }
            selectedMedias.push({ file: outFile, caption: captions[i] });
        }
        onSend(selectedMedias);
        if (!uploading) handleClose();
    };

    const renderFileContent = useMemo(() => {
        const currentFile = files[currentIndex];
        if (!currentFile) return null;
        if (currentFile.type.startsWith('image')) {
                return (
                    <>
                        <div className={classes.headerBar}>
                            <Typography variant="subtitle2">{currentFile.name}</Typography>
                            <Typography variant="caption">{currentIndex + 1} / {files.length}</Typography>
                        </div>
                        <div className={classes.canvasWrap}>
                            <canvas
                                ref={canvasRef}
                                width={600}
                                height={400}
                                onMouseDown={(e) => {
                                    const tool = edits[currentIndex]?.tool;
                                    const t = edits[currentIndex]?.text;
                                    const ctx = getCtx();
                                    if (!ctx) return;
                                    const p = getPointer(e);
                                    if (!p) return;
                                    isMouseDownRef.current = true;
                                    // drag texto
                                    if (tool === 'text' && t?.value) {
                                        const fontSize = Math.max(12, Math.min(48, t.size || 28));
                                        ctx.font = `${fontSize}px sans-serif`;
                                        const textW = ctx.measureText(t.value).width;
                                        const textH = fontSize;
                                        const tx = (t.x ?? 0.85) * canvasRef.current.width;
                                        const ty = (t.y ?? 0.90) * canvasRef.current.height;
                                        if (p.x >= tx - 6 && p.x <= tx + textW + 6 && p.y >= ty - textH - 6 && p.y <= ty + 6) {
                                            draggingTextRef.current = true;
                                            return;
                                        }
                                    }
                                    // iniciar forma
                                    if (['line','arrow','rect','circle','redact'].includes(tool)) {
                                        const color = edits[currentIndex]?.shapeColor || '#FF0000';
                                        const sw = edits[currentIndex]?.strokeWidth || 3;
                                        const fill = !!edits[currentIndex]?.fillShape;
                                        currentShapeRef.current = {
                                            type: tool,
                                            color,
                                            strokeWidth: sw,
                                            fill,
                                            start: { x: p.nx, y: p.ny },
                                            end: { x: p.nx, y: p.ny }
                                        };
                                    }
                                }}
                                onMouseMove={(e) => {
                                    const tool = edits[currentIndex]?.tool;
                                    const p = getPointer(e);
                                    if (!p || !isMouseDownRef.current) return;
                                    const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
                                    if (now - (lastMoveTsRef.current || 0) < 16) return; // ~60fps
                                    lastMoveTsRef.current = now;
                                    if (draggingTextRef.current) {
                                        setEdits(prev => {
                                            const next = [...prev];
                                            const ed = { ...next[currentIndex] };
                                            const t = { ...ed.text };
                                            t.x = Math.max(0, Math.min(1, p.nx));
                                            t.y = Math.max(0, Math.min(1, p.ny));
                                            ed.text = t;
                                            next[currentIndex] = ed;
                                            return next;
                                        });
                                        return;
                                    }
                                    if (['line','arrow','rect','circle','redact'].includes(tool) && currentShapeRef.current) {
                                        currentShapeRef.current = { ...currentShapeRef.current, end: { x: p.nx, y: p.ny } };
                                        // forçar redesenho
                                        setEdits(prev => [...prev]);
                                    }
                                }}
                                onMouseUp={() => {
                                    isMouseDownRef.current = false;
                                    if (draggingTextRef.current) {
                                        draggingTextRef.current = false;
                                        return;
                                    }
                                    if (currentShapeRef.current) {
                                        const shape = currentShapeRef.current;
                                        currentShapeRef.current = null;
                                        setEdits(prev => {
                                            const next = [...prev];
                                            const ed = { ...next[currentIndex] };
                                            ed.shapes = [...(ed.shapes || []), shape];
                                            next[currentIndex] = ed;
                                            return next;
                                        });
                                    }
                                }}
                            />
                        </div>
                        <div className={classes.toolsBar}>
                            <Tooltip title="Girar 90° anti-horário"><span><IconButton onClick={() => { setShowTextOptions(false); setShowShapeOptions(false); rotateBy(-90); }} disabled={uploading}><RotateLeft /></IconButton></span></Tooltip>
                            <Tooltip title="Girar 90° horário"><span><IconButton onClick={() => { setShowTextOptions(false); setShowShapeOptions(false); rotateBy(90); }} disabled={uploading}><RotateRight /></IconButton></span></Tooltip>
                            <Tooltip title="Espelhar horizontal"><span><IconButton onClick={() => { setShowTextOptions(false); setShowShapeOptions(false); toggleFlipH(); }} disabled={uploading}><Flip /></IconButton></span></Tooltip>
                            <Tooltip title="Espelhar vertical"><span><IconButton onClick={() => { setShowTextOptions(false); setShowShapeOptions(false); toggleFlipV(); }} disabled={uploading}><Flip style={{ transform: 'rotate(90deg)' }} /></IconButton></span></Tooltip>
                            <Tooltip title="Ferramenta: texto"><span><IconButton onClick={() => { updateEdit({ tool: 'text' }); setShowTextOptions(v => !v); setShowShapeOptions(false); }} disabled={uploading}><TextFields /></IconButton></span></Tooltip>
                            <Tooltip title="Linha"><span><IconButton onClick={() => { updateEdit({ tool: 'line' }); setShowShapeOptions(true); setShowTextOptions(false); }} disabled={uploading}><ShowChart /></IconButton></span></Tooltip>
                            <Tooltip title="Seta"><span><IconButton onClick={() => { updateEdit({ tool: 'arrow' }); setShowShapeOptions(true); setShowTextOptions(false); }} disabled={uploading}><CallMade /></IconButton></span></Tooltip>
                            <Tooltip title="Retângulo"><span><IconButton onClick={() => { updateEdit({ tool: 'rect' }); setShowShapeOptions(true); setShowTextOptions(false); }} disabled={uploading}><CropSquare /></IconButton></span></Tooltip>
                            <Tooltip title="Círculo"><span><IconButton onClick={() => { updateEdit({ tool: 'circle' }); setShowShapeOptions(true); setShowTextOptions(false); }} disabled={uploading}><RadioButtonUnchecked /></IconButton></span></Tooltip>
                            <Tooltip title="Tarja (cobrir)"><span><IconButton onClick={() => { updateEdit({ tool: 'redact', fillShape: true, shapeColor: '#000000' }); setShowShapeOptions(true); setShowTextOptions(false); }} disabled={uploading}><Stop /></IconButton></span></Tooltip>
                            <Tooltip title="Desfazer última forma"><span><IconButton onClick={() => setEdits(prev => { const next = [...prev]; const ed = { ...next[currentIndex] }; ed.shapes = (ed.shapes || []).slice(0, -1); next[currentIndex] = ed; return next; })} disabled={uploading}><Undo /></IconButton></span></Tooltip>
                            <Tooltip title="Limpar formas"><span><IconButton onClick={() => setEdits(prev => { const next = [...prev]; const ed = { ...next[currentIndex] }; ed.shapes = []; next[currentIndex] = ed; return next; })} disabled={uploading}><DeleteForever /></IconButton></span></Tooltip>
                        </div>
                        {showTextOptions && (
                            <div className={classes.textOptions}>
                                <div className={classes.optionsContainer}>
                                    {/* Linha 1: caixa de texto */}
                                    <TextField
                                        label="Texto"
                                        size="small"
                                        variant="outlined"
                                        value={edits[currentIndex]?.text?.value || ''}
                                        onChange={(e) => setTextValue(e.target.value)}
                                        fullWidth
                                    />

                                    {/* Linha 2: ícones e paletas (centralizado) */}
                                    <div className={classes.optionsRow}>
                                    {/* Tamanho com - [n] + */}
                                    <Tooltip title="Diminuir tamanho"><span><IconButton size="small" onClick={() => incTextSize(-2)} disabled={uploading}><Remove /></IconButton></span></Tooltip>
                                    <InputBase
                                        className={classes.smallNumberBox}
                                        type="number"
                                        inputProps={{ min: 12, max: 128, step: 1 }}
                                        value={edits[currentIndex]?.text?.size || 28}
                                        onChange={(e) => setTextSize(clamp(parseInt(e.target.value || '0', 10), 12, 128))}
                                    />
                                    <Tooltip title="Aumentar tamanho"><span><IconButton size="small" onClick={() => incTextSize(2)} disabled={uploading}><Add /></IconButton></span></Tooltip>

                                    {/* Paleta de cores do texto */}
                                    <div className={classes.dotPalette}>
                                        {TEXT_COLORS.map((c) => {
                                            const isSel = (edits[currentIndex]?.text?.color || '#FFFFFF') === c;
                                            return (
                                                <div
                                                    key={c}
                                                    onClick={() => setTextColor(c)}
                                                    style={{ background: c, borderColor: c === '#FFFFFF' ? '#bbb' : undefined }}
                                                    className={`${classes.colorDot} ${isSel ? classes.colorDotSelected : ''}`}
                                                />
                                            );
                                        })}
                                    </div>

                                    {/* Fundo do texto ON/OFF */}
                                    <Tooltip title={edits[currentIndex]?.text?.bg ? 'Fundo: ON' : 'Fundo: OFF'}>
                                        <span>
                                            <IconButton size="small" onClick={() => setTextBg(!edits[currentIndex]?.text?.bg)} disabled={uploading}>
                                                <FormatColorFill style={{ color: edits[currentIndex]?.text?.bg ? (theme.palette.primary.main) : (theme.palette.text.secondary) }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    </div>
                                    <div className={classes.hintCentered}>
                                        <Typography variant="caption">Dica: selecione "Texto" e arraste o texto diretamente no canvas.</Typography>
                                    </div>
                                </div>
                            </div>
                        )}
                        {showShapeOptions && (
                            <div className={classes.textOptions}>
                                {/* Opções de forma */}
                                <div className={classes.optionsRow}>
                                    {/* Para TARJA, travar preenchimento ON e ocultar espessura */}
                                    {edits[currentIndex]?.tool !== 'redact' && (
                                        <>
                                            <Tooltip title="Diminuir espessura"><span><IconButton size="small" onClick={() => incStrokeWidth(-1)} disabled={uploading}><Remove /></IconButton></span></Tooltip>
                                            <InputBase
                                                className={classes.smallNumberBox}
                                                type="number"
                                                inputProps={{ min: 1, max: 10, step: 1 }}
                                                value={edits[currentIndex]?.strokeWidth || 3}
                                                onChange={(e) => setStrokeWidth(parseInt(e.target.value || '0', 10))}
                                            />
                                            <Tooltip title="Aumentar espessura"><span><IconButton size="small" onClick={() => incStrokeWidth(1)} disabled={uploading}><Add /></IconButton></span></Tooltip>
                                        </>
                                    )}

                                    {/* Paleta de cores das formas */}
                                    <div className={classes.dotPalette}>
                                        {SHAPE_COLORS.map((c) => {
                                            const isSel = (edits[currentIndex]?.shapeColor || '#FF0000') === c;
                                            return (
                                                <div
                                                    key={c}
                                                    onClick={() => updateEdit({ shapeColor: c })}
                                                    style={{ background: c, borderColor: c === '#FFFFFF' ? '#bbb' : undefined }}
                                                    className={`${classes.colorDot} ${isSel ? classes.colorDotSelected : ''}`}
                                                />
                                            );
                                        })}
                                    </div>

                                    {/* Preenchimento ON/OFF (oculto em tarja) */}
                                    {edits[currentIndex]?.tool !== 'redact' ? (
                                        <Tooltip title={edits[currentIndex]?.fillShape ? 'Preenchimento: ON' : 'Preenchimento: OFF'}>
                                            <span>
                                                <IconButton size="small" onClick={() => updateEdit({ fillShape: !edits[currentIndex]?.fillShape })} disabled={uploading}>
                                                    <FormatColorFill style={{ color: edits[currentIndex]?.fillShape ? (theme.palette.primary.main) : (theme.palette.text.secondary) }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Preenchimento 100% habilitado">
                                            <span>
                                                <IconButton size="small" disabled>
                                                    <FormatColorFill style={{ color: theme.palette.primary.main }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                );
        } else if (currentFile.type === 'application/pdf') {
                return (
                    <>
                        <div className={classes.headerBar}>
                            <Typography variant="subtitle2">{currentFile.name}</Typography>
                            <Typography variant="caption">{currentIndex + 1} / {files.length}</Typography>
                        </div>
                        <div className={classes.canvasWrap}>
                            <Document file={URL.createObjectURL(currentFile)} onLoadSuccess={onDocumentLoadSuccess} >
                                <Page pageNumber={1}
                                    width={300}
                                    height={360}
                                />
                            </Document>
                        </div>
                    </>
                );
        } else if (currentFile.type.startsWith('video')) {
                return (
                    <>
                        <div className={classes.headerBar}>
                            <Typography variant="subtitle2">{currentFile.name}</Typography>
                            <Typography variant="caption">{currentIndex + 1} / {files.length}</Typography>
                        </div>
                        <div className={classes.canvasWrap}>
                            <video
                                src={URL.createObjectURL(currentFile)}
                                controls={true}
                                volume={localStorage.getItem("volume")}
                                style={{ maxWidth: 600, maxHeight: 400, borderRadius: 8 }}
                            />
                        </div>
                    </>
                );
        } else if (currentFile.type.startsWith('audio')) {
                return (
                    <>
                        <div className={classes.headerBar}>
                            <Typography variant="subtitle2">{currentFile.name}</Typography>
                            <Typography variant="caption">{currentIndex + 1} / {files.length}</Typography>
                        </div>
                        <div className={classes.canvasWrap}>
                            <AudioModal url={URL.createObjectURL(currentFile)} />
                        </div>
                    </>
                );
        } else {
                return (
                    <>
                        <div className={classes.headerBar}>
                            <Typography variant="subtitle2">{currentFile.name}</Typography>
                            <Typography variant="caption">{currentIndex + 1} / {files.length}</Typography>
                        </div>
                        <div className={classes.canvasWrap}>
                            <CardContent className={classes.modal}>
                                <Typography variant="h6">Visualização não disponível</Typography>
                            </CardContent>
                        </div>
                    </>
                );
        }
    }, [currentIndex, files, showTextOptions, uploading, edits]);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && event.shiftKey) {
            // const newCaptions = captions.slice();
            // newCaptions[currentIndex] += '\n';
            // setCaptions(newCaptions);
            return
        }
        switch (event.key) {
            case 'Escape':
                onCancelSelection();
                break;
            case 'Enter':
                handleSend();
                break;
            case 'ArrowRight':
                handleNext();
                break;
            case 'ArrowLeft':
                handlePrev();
                break;
            default:
                break;
        }
    };
    return (
        <div className={classes.modal}>
            <Dialog
                open={isOpen}
                fullWidth
                maxWidth="md"
                scroll="paper"
            >
                <DialogContent className={classes.modal}>
                    <Card style={{ borderRadius: 12, overflow: 'hidden' }}>
                        {renderFileContent}
                        <CardContent className={classes.modal}>
                            <div className={classes.messageInputWrapperPrivate}>
                                <InputBase
                                    placeholder="Legenda (opcional)"
                                    fullWidth
                                    multiline
                                    minRows={1}
                                    maxRows={5}
                                    value={captions[currentIndex]}
                                    onChange={handleCaptionChange}
                                    onBlur={handleTextFieldBlur}
                                    autoFocus
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                            {uploading && progressList?.length > 0 && (
                                <div style={{ marginTop: 8 }}>
                                    <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, progressList[currentIndex] || 0))} />
                                    <div style={{ marginTop: 4, textAlign: 'center', fontSize: 12, color: '#888' }}>
                                        {Math.round(progressList[currentIndex] || 0)}%
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </DialogContent>
                <DialogActions className={classes.modal}>
                    <IconButton onClick={handlePrev} disabled={currentIndex === 0 || uploading}>
                        <SkipPrevious className={currentIndex === 0 ? classes.buttonDisable : classes.buttonEnable} />
                    </IconButton>
                    <IconButton onClick={onCancelSelection} disabled={uploading}>
                        <Cancel className={classes.buttonEnable} />
                    </IconButton>
                    {!uploading ? (
                        <IconButton onClick={handleSend}>
                            <Send className={classes.buttonEnable} />
                        </IconButton>
                    ) : (
                        <IconButton onClick={() => onCancelUpload && onCancelUpload(currentIndex)}>
                            <Cancel className={classes.buttonEnable} />
                        </IconButton>
                    )}
                    <IconButton onClick={handleNext} disabled={currentIndex === files.length - 1 || uploading}>
                        <SkipNext className={currentIndex === files.length - 1 ? classes.buttonDisable : classes.buttonEnable} />
                    </IconButton>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default MessageUploadMedias;
